import React from 'react';
import { ThemeProvider } from '@mui/material';

import { FeatureLeaderboard } from '@models/feature-leaderboard';
import { TableColumns } from '@models/column';
import { FeatureId } from '@models/feature';
import { isNotSet, isSet, sha256 } from '@utils';
import { theme } from '@utils/widget-theme';
import { WidgetProps, useModelState } from '@utils/widget-model';
import { compose, filter } from '@utils/transducer';

import {
  DrawerMode,
  FeatureLeaderboardEntryDrawer,
} from './leaderboard-entry/FeatureLeaderboardEntryDrawer';
import { FeatureLeaderboardWidgetState } from '../FeatureLeaderboardWidget';
import { FeatureLeaderboardOverview } from './FeatureLeaderboardOverview';
import { FeatureActions } from './leaderboard-entry/feature-leaderboard-entry-drawer-header';

export const FeatureLeaderboardView: React.FC<WidgetProps> = ({ model }) => {
  const [featureLeaderboard] = useModelState<
    FeatureLeaderboardWidgetState,
    'value'
  >('value');
  const isWithAssessment =
    isNotSet(featureLeaderboard.isAssessed) || featureLeaderboard.isAssessed;
  const [confidence, setConfidence] = useModelState<
    FeatureLeaderboardWidgetState,
    'confidence'
  >('confidence');

  const [maxCollinearity, setMaxCollinearity] = useModelState<
    FeatureLeaderboardWidgetState,
    'max_collinearity'
  >('max_collinearity');

  const [explanationSearch, setExplanationSearch] = React.useState(() => '');

  const featureEntries = React.useMemo(
    () =>
      filterFeatures(
        featureLeaderboard,
        maxCollinearity,
        confidence,
        explanationSearch,
        isWithAssessment
      ),
    [maxCollinearity, confidence, explanationSearch, featureLeaderboard]
  );

  const tableColumns = React.useMemo(
    () =>
      featureLeaderboard.tableColumns
        ? TableColumns.fromSimplified(featureLeaderboard.tableColumns)
        : {},
    [featureLeaderboard.tableColumns]
  );

  const [copiedFeature, setCopiedFeature] = React.useState<
    { feature: FeatureLeaderboard.Entry; parentId: FeatureId } | undefined
  >(() => undefined);

  return (
    <ThemeProvider theme={theme}>
      <FeatureLeaderboardOverview
        isWithAssessment={isWithAssessment}
        confidence={confidence}
        setConfidence={setConfidence}
        maxCollinearity={maxCollinearity}
        setMaxCollinearity={setMaxCollinearity}
        setSearch={setExplanationSearch}
      />
      <br />

      <div className="feature-entries-header" style={{ display: 'flex' }}>
        <span
          className="feature-id"
          style={{ marginLeft: isWithAssessment ? '30px' : 0 }}
        >
          Feature ID
        </span>
        {isWithAssessment ? (
          <span className="confidence">Confidence</span>
        ) : null}
        <span className="explanation">Explanation</span>
      </div>

      <div
        style={{
          display: 'inline-block',
          minWidth: '100%',
          marginBottom: '2px',
        }}
      >
        {featureEntries.map(featureEntry => (
          <>
            <FeatureLeaderboardEntryDrawer
              key={featureEntry.feature.id}
              featureEntry={featureEntry}
              originFeatureId={featureEntry.feature.id}
              tableColumn={tableColumns}
              isWithAssessment={isWithAssessment}
              onModelChange={mode => {
                if (mode === DrawerMode.Copy) {
                  const originId = FeatureId.getOriginId(
                    featureEntry.feature.id
                  );

                  sha256(`${new Date()}_${featureEntry.feature.id}`, 8).then(
                    id => {
                      const feature = {
                        ...featureEntry,
                        isAssessed: false,
                        feature: {
                          ...featureEntry.feature,
                          id: `${originId}_${id}`,
                        },
                      };
                      setCopiedFeature({
                        feature: {
                          ...feature,
                          signalConfidence: 0,
                        },
                        parentId: featureEntry.feature.id,
                      });
                    }
                  );
                }
                return mode !== DrawerMode.Copy;
              }}
              isWithActions={isWithActions(featureEntry, isWithAssessment)}
            />
            {copiedFeature?.parentId === featureEntry.feature.id ? (
              <div style={{ marginBottom: '20px' }}>
                <FeatureLeaderboardEntryDrawer
                  featureEntry={copiedFeature.feature}
                  originFeatureId={copiedFeature.parentId}
                  tableColumn={tableColumns}
                  isWithAssessment={isWithAssessment}
                  initMode={DrawerMode.Copy}
                  onCancel={() => setCopiedFeature(undefined)}
                  isWithLikes={false}
                  isWithActions={false}
                />
              </div>
            ) : null}
          </>
        ))}
      </div>
    </ThemeProvider>
  );
};

function isWithActions(
  featureEntry: FeatureLeaderboard.Entry,
  isWithAssessment: boolean
): FeatureActions | boolean {
  return isWithAssessment ? true : { code: false, copy: false, edit: false };
}

function filterFeatures(
  featureLeaderboard: FeatureLeaderboard,
  maxCollinearity: number,
  confidence: number,
  explanationSearchPhrase: string,
  isWithAssessment: boolean
): FeatureLeaderboard.Entry[] {
  const shouldCheckIsAssessed = (entry: FeatureLeaderboard.Entry) =>
    isSet(entry.isAssessed) ? entry.isAssessed : true;

  const explanationSearchFilter = (entry: FeatureLeaderboard.Entry) =>
    !explanationSearchPhrase ||
    entry.feature.explanation_str?.includes(explanationSearchPhrase);
  const correlationFilter = (entry: FeatureLeaderboard.Entry) =>
    shouldCheckIsAssessed(entry)
      ? entry.maxAbsCorrelationOfSimplerFeature <= maxCollinearity
      : true;
  const confidenceFilter = (entry: FeatureLeaderboard.Entry) =>
    shouldCheckIsAssessed(entry) ? entry.signalConfidence >= confidence : true;

  const reduces = isWithAssessment
    ? compose(
        filter(explanationSearchFilter),
        filter(correlationFilter),
        filter(confidenceFilter)
      )
    : compose(filter(explanationSearchFilter));

  const aggregation = (
    a: FeatureLeaderboard.Entry[],
    b: FeatureLeaderboard.Entry
  ) => a.concat([b]);

  return featureLeaderboard.allEntries
    .flat()
    .reduce<FeatureLeaderboard.Entry[]>(reduces(aggregation), []);
}
